import { useEffect, useState } from "react";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { TableColumn } from "react-data-table-component";
import { useLocation, useParams } from "react-router-dom";
import { useGetActionPlans } from "../../api/ActionPlanApi";
import getIcon, { Icon, IconSize } from "../../assets/Icons/IconClassNames";
import ClickableIconButton from "../../components/ClickableIconButton/ClickableIconButton";
import ContentContainer from "../../components/ContainerComponents/ContentContainer";
import HeaderContainer from "../../components/ContainerComponents/HeaderContainer";
import MainContainer from "../../components/ContainerComponents/MainContainer";
import CustomDropdown, { DropdownToggleType } from "../../components/CustomDropdown/CustomDropdown";
import { CustomDropdownItem } from "../../components/CustomDropdown/CustomDropdownItem/CustomDropdownItem";
import PaginatedDataTableClientSide from "../../components/DataTableComponents/PaginatedDataTable";
import When from "../../components/When";
import { ActionPlanStatus, ActionPlanType, CompanyRole } from "../../core/enums/enums";
import { DateToYYYYMMDD } from "../../core/helpers/dates/datesHelper";
import { IActionPlan } from "../../core/interfaces/actionPlan";
import useAccessCheck from "../../hooks/useAccessCheck";
import NewIncidentModal from "../Dashboard/Components/Incident/NewIncidentModal";
import ActionPlanModal from "./ActionPlanModal";
import RemoveActionPlanModal from "./Components/RemoveActionPlanModal";

const EnumToValue: { [key in keyof typeof ActionPlanType]: string } = {
    [ActionPlanType.INCIDENT]: 'Tillbud',
    [ActionPlanType.ACCIDENT]: 'Olycka',
    [ActionPlanType.DERIVATION]: 'Avvikelse',
    [ActionPlanType.STANDALONE]: 'Egen handling',
    CHECKLIST: "Checklista"
};

const StatusValue: { [key in keyof typeof ActionPlanStatus]: string } = {
    CREATED: "Nyinkommen",
    ONGOING: "Påbörjad",
    COMPLETED: "Slutförd"
};

const ActionPlanView = () => {
    const location = useLocation();
    const {cId} = useParams();
    const [data, isFetching, isFetched] = useGetActionPlans();
    const [chosen, setChosen] = useState<IActionPlan | null>(null);
    const [openActionPlanModal, setOpenActionPlanModal] = useState(false);
    const [openStandaloneActionModal, setOpenStandaloneActionModal] = useState(false);
    const [showRemoveActionPlanModal, setShowRemoveActionPlanModal] = useState(false);
    const [checkAccess] = useAccessCheck();
    const hide = () => {
        setChosen(null);
        setOpenActionPlanModal(false);
        setOpenStandaloneActionModal(false);
        setShowRemoveActionPlanModal(false);
    }

    const [filterText, setFilterText] = useState("");
    const columns: TableColumn<IActionPlan>[] = [
        {
            name: 'RAPPORTERAT',
            selector: cell => DateToYYYYMMDD(cell.createdAt),
            sortable: true
        },
        {
            name: "RAPPORTERAT AV",
            selector: row => row.reportedByUser?.firstName + " " + row.reportedByUser?.lastName,
            sortable: true,
        },
        {
            name: "TILLDELAD",
            selector: row => row.actionUser?.firstName + " " + row.actionUser?.lastName,
            sortable: true,
        },
        {
            name: 'ÅTGÄRDAD SENAST',
            selector: cell => DateToYYYYMMDD(cell.actionDate),
            sortable: true
        },
        {
            name: "TYP",
            selector: c => EnumToValue[(c.type) as ActionPlanType],
            sortable: true,
            compact: true,
        },
        {
            name: "BESKRIVNING",
            cell: cell => cell.headDescription,
            sortable: true,
            compact: true,
        },
        {
            name: "STATUS",
            selector: row => StatusValue[(row.status) as ActionPlanStatus],
            sortable: true,
            compact: true,
        },
        {
            name: "DOKUMENT",
            cell: cell => <a href={`/c/${cId}/documents/${cell.relatedDocument?.id}`} target="_blank"
                rel="noreferrer">{cell.relatedDocument?.documentName}</a>,
            sortable: false,
        },
        {
            name: <></>,
            right: true,
            cell: row => (
                <CustomDropdown toggleType={DropdownToggleType.GEAR}>
                    <CustomDropdownItem
                        icon={Icon.EDIT}
                        text={"Redigera"}
                        onClick={() => {
                            setChosen(row);
                            setOpenActionPlanModal(true);
                        }}
                    />
                    <When condition={checkAccess(CompanyRole.ADMIN)}>
                        <CustomDropdownItem
                            icon={Icon.REMOVE}
                            text={"Radera"}
                            onClick={() => {
                                setChosen(row);
                                setShowRemoveActionPlanModal(true);
                            }}
                        />
                    </When>
                </CustomDropdown>
            )
        }
    ]

    function ActionPlanTab(type: ActionPlanType[] | ActionPlanType) {
        const isCheckList = Array.isArray(type) ? type.includes(ActionPlanType.CHECKLIST || ActionPlanType.STANDALONE) : type === (ActionPlanType.CHECKLIST || ActionPlanType.STANDALONE);

        const dataByType = data.filter(x => Array.isArray(type) ? type.includes(x.type) : x.type === type)

        const extraColumn: TableColumn<IActionPlan>[] = isCheckList ? [] : [{
            name: "LÖPNUMMER",
            cell: row => row.serialNumber ?? '-',
            selector: row => row.serialNumber ?? "",
            sortable: true,
            compact: true,
        }]
        const filteredItems = dataByType.filter(
            item => (
                (item.serialNumber?.toString().toLowerCase().includes(filterText.toLowerCase())) ||
                (item.reportedByUser?.firstName && item.reportedByUser?.firstName.toLowerCase().includes(filterText.toLowerCase())) ||
                (item.reportedByUser?.lastName && item.reportedByUser?.lastName.toLowerCase().includes(filterText.toLowerCase())) ||
                (item.actionUser?.firstName && item.actionUser?.firstName.toLowerCase().includes(filterText.toLowerCase())) ||
                (item.actionUser?.lastName && item.actionUser?.lastName.toLowerCase().includes(filterText.toLowerCase())) ||
                (item.headDescription?.toLowerCase().includes(filterText.toLowerCase())) ||
                (item.status && StatusValue[(item.status) as ActionPlanStatus].toLowerCase().includes(filterText.toLowerCase())) ||
                (item.actionDate && item.actionDate.toLowerCase().includes(filterText.toLowerCase())) ||
                (item.createdAt && item.createdAt.toLowerCase().includes(filterText.toLowerCase())) ||
                (item.type && EnumToValue[(item.type)].toLowerCase().includes(filterText.toLowerCase())) ||
                (filterText.includes(' ') &&
                    (item.reportedByUser && (item.reportedByUser.firstName.toLowerCase().includes(filterText.split(' ')[0].toLowerCase()) && item.reportedByUser.lastName.toLowerCase().includes(filterText.split(' ')[1].toLowerCase())))) ||
                (filterText.includes(' ') &&
                    (item.actionUser && (item.actionUser.firstName.toLowerCase().includes(filterText.split(' ')[0].toLowerCase()) && item.actionUser.lastName.toLowerCase().includes(filterText.split(' ')[1].toLowerCase()))))
            ),
        );

        useEffect(() => {
            if (isFetched) {
                const stateId = location.state?.stateId;
                const chosenItem = stateId ? filteredItems.find(item => item.id === stateId) : null;

                if (chosenItem) {
                    setChosen(chosenItem);
                    setOpenActionPlanModal(true);
                }
            }

        }, [isFetched]);

        return (
            <>
                <ContentContainer>
                    <When condition={type.includes(ActionPlanType.CHECKLIST || ActionPlanType.STANDALONE)}>
                        <div className="position-absolute ps-3" style={{zIndex: 10}}>
                            <ClickableIconButton
                                disabled={false}
                                buttonType={"EDIT"}
                                icon={getIcon(Icon.CIRCLE_PLUS, IconSize.X2)}
                                onClick={() => setOpenStandaloneActionModal(true)}
                                title={"Lägg till handling"}
                            />
                        </div>
                    </When>
                    <PaginatedDataTableClientSide
                        data={filteredItems ?? []}
                        progressPending={isFetching}
                        columns={[...extraColumn, ...columns]}
                        pointerOnHover
                        withFilter
                        filterText={filterText}
                        setFilterText={(prev) => setFilterText(prev)}
                        onRowClick={v => { setChosen(v); setOpenActionPlanModal(true) }}
                    />
                </ContentContainer>
            </>
        );
    }

    return (
        <MainContainer>
            <HeaderContainer title={"Handlingsplan"}/>
            <Tabs
                defaultActiveKey="INCIDENT"
                transition={false}
                className="ms-5 me-5 shadow"
                justify
            >
                <Tab eventKey="INCIDENT" title="Olyckor och tillbud">
                    {ActionPlanTab([ActionPlanType.INCIDENT, ActionPlanType.ACCIDENT])}
                </Tab>
                <Tab eventKey="DERIVATION" title="Avvikelse">
                    {ActionPlanTab(ActionPlanType.DERIVATION)}
                </Tab>
                <Tab eventKey="CHECKLIST" title="Handlingsplan">
                    {ActionPlanTab([ActionPlanType.CHECKLIST, ActionPlanType.STANDALONE])}
                </Tab>
            </Tabs>

            <ActionPlanModal
                actionPlan={chosen}
                show={openActionPlanModal}
                setShow={() => setOpenActionPlanModal(false)}
            />

            <NewIncidentModal
                initialActionPlanType={ActionPlanType.STANDALONE}
                show={openStandaloneActionModal}
                setShow={() => setOpenStandaloneActionModal(false)}
            />

            <RemoveActionPlanModal
                actionPlan={chosen}
                hide={hide}
                show={showRemoveActionPlanModal} />
        </MainContainer>
    )
}

export default ActionPlanView

